import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "../../components/ui/sonner";
import { RefreshCw, Trash2, ShieldCheck, ShieldOff } from "lucide-react";

const ALL_CURRENCIES = ["INR", "USD", "AED", "AUD", "CAD", "GBP", "EUR", "CHF", "QAR", "MYR"];

const Field = ({ label, children }) => (
  <div>
    <label className="block font-mono text-[9px] tracking-widest text-slate-500 mb-2">{label}</label>
    {children}
  </div>
);

export default function Settings() {
  const [form, setForm] = useState(null);
  const [fx, setFx] = useState(null);
  const [payConfig, setPayConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((res) => setForm(res.data)).catch(() => toast.error("Could not load settings"));
    api.get("/fx/rates").then((res) => setFx(res.data)).catch(() => {});
    api.get("/checkout/config").then((res) => setPayConfig(res.data)).catch(() => {});
  };
  useEffect(load, []);

  if (!form) return <div className="w-8 h-8 border-2 border-flame border-t-transparent rounded-full animate-spin" />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setRate = (k) => (e) => setForm({ ...form, rates: { ...form.rates, [k]: parseInt(e.target.value || "0", 10) } });
  const setSect = (sect, k, v) => setForm({ ...form, [sect]: { ...form[sect], [k]: v } });
  const toggleIn = (sect, key, code) => {
    const list = form[sect][key] || [];
    const next = list.includes(code) ? list.filter((c) => c !== code) : [...list, code];
    if (sect === "payments" && code === "INR" && list.includes("INR")) return; // INR always payable
    setSect(sect, key, next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        whatsapp_number: form.whatsapp_number || "",
        contact_email: form.contact_email || "",
        phone: form.phone || "",
        address: form.address || "",
        working_hours: form.working_hours || "",
        privacy_email: form.privacy_email || "",
        rates: form.rates,
        currency: form.currency,
        tax: form.tax,
        payments: form.payments,
      });
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const refreshFx = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/admin/fx/refresh");
      setFx(res.data);
      toast.success("Exchange rates refreshed");
    } catch {
      toast.error("FX refresh failed — cached rates remain in use");
    } finally {
      setRefreshing(false);
    }
  };

  const purgeDemo = async () => {
    try {
      const res = await api.delete("/admin/demo-data");
      toast.success(`Removed ${res.data.leads_removed} leads, ${res.data.bookings_removed} bookings, ${res.data.case_studies_removed} case studies`);
    } catch {
      toast.error("Purge failed");
    }
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-3xl">
      <h1 className="font-heading text-3xl font-extrabold text-white">Settings</h1>
      <p className="mt-2 text-sm text-slate-400">Business-critical values are configured here, never hard-coded.</p>

      {/* Contact */}
      <div className="mt-10 rounded-xl border border-white/10 bg-navy-800 p-8 space-y-6">
        <p className="font-heading font-bold text-white">Contact details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="WHATSAPP NUMBER (country code, digits only)"><Input value={form.whatsapp_number || ""} onChange={set("whatsapp_number")} data-testid="settings-whatsapp" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="CONTACT EMAIL"><Input value={form.contact_email || ""} onChange={set("contact_email")} data-testid="settings-email" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="PRIVACY CONTACT EMAIL"><Input value={form.privacy_email || ""} onChange={set("privacy_email")} data-testid="settings-privacy-email" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="PHONE"><Input value={form.phone || ""} onChange={set("phone")} data-testid="settings-phone" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="WORKING HOURS"><Input value={form.working_hours || ""} onChange={set("working_hours")} data-testid="settings-hours" className="bg-navy-900/60 border-white/10" /></Field>
        </div>
        <Field label="BUSINESS ADDRESS"><Input value={form.address || ""} onChange={set("address")} data-testid="settings-address" className="bg-navy-900/60 border-white/10" /></Field>
      </div>

      {/* Rates */}
      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 p-8 space-y-6">
        <p className="font-heading font-bold text-white">Hourly rates (base currency: INR)</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Field label="STANDARD DELIVERY"><Input type="number" value={form.rates?.standard ?? ""} onChange={setRate("standard")} data-testid="settings-rate-standard" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="VIBE CODING SPECIALIST"><Input type="number" value={form.rates?.specialist ?? ""} onChange={setRate("specialist")} data-testid="settings-rate-specialist" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="SUPER VIBE CODER"><Input type="number" value={form.rates?.super ?? ""} onChange={setRate("super")} data-testid="settings-rate-super" className="bg-navy-900/60 border-white/10" /></Field>
        </div>
        <p className="text-[11px] text-slate-500">Foreign-currency prices are always calculated dynamically from these INR base rates — never stored as independent fixed prices.</p>
      </div>

      {/* Currency */}
      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 p-8 space-y-6" data-testid="settings-currency-section">
        <div className="flex items-center justify-between">
          <p className="font-heading font-bold text-white">Currency</p>
          <Button onClick={refreshFx} disabled={refreshing} data-testid="settings-fx-refresh" variant="outline" className="border-white/20 text-slate-200">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> REFRESH RATES
          </Button>
        </div>
        {fx && (
          <p className="font-mono text-[11px] text-slate-400" data-testid="settings-fx-status">
            Exchange rates updated: {fx.updated_at ? new Date(fx.updated_at).toLocaleString("en-IN") : "never"} · source: {fx.source}
            {fx.source === "unavailable" && <span className="text-amber-400"> — live conversion temporarily unavailable, INR shown</span>}
          </p>
        )}
        <Field label="DISPLAY CURRENCIES (base pricing is always INR)">
          <div className="flex flex-wrap gap-2 mt-1">
            {ALL_CURRENCIES.map((c) => (
              <button key={c} type="button" data-testid={`settings-display-currency-${c}`} onClick={() => toggleIn("currency", "display_currencies", c)}
                className={`rounded-full px-4 py-2 font-mono text-[11px] font-bold tracking-wider transition-colors ${(form.currency?.display_currencies || []).includes(c) ? "bg-flame text-white" : "border border-white/15 text-slate-400"}`}>
                {c}
              </button>
            ))}
          </div>
        </Field>
        <Field label="RATE REFRESH INTERVAL (MINUTES)">
          <Input type="number" min={5} max={60} value={form.currency?.refresh_minutes ?? 10} onChange={(e) => setSect("currency", "refresh_minutes", parseInt(e.target.value || "10", 10))} data-testid="settings-refresh-minutes" className="bg-navy-900/60 border-white/10 w-40" />
        </Field>
        <p className="text-[11px] text-slate-500">FX provider is configured via FX_API_URL / FX_API_KEY in the server environment. If the provider is unavailable, the most recent cached rate is used automatically.</p>
      </div>

      {/* Tax */}
      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 p-8 space-y-6" data-testid="settings-tax-section">
        <p className="font-heading font-bold text-white">Tax</p>
        <label className="flex items-center gap-3 text-sm text-slate-200">
          <Switch checked={!!form.tax?.gst_enabled} onCheckedChange={(v) => setSect("tax", "gst_enabled", v)} data-testid="settings-gst-toggle" /> GST enabled
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="GST RATE (%)"><Input type="number" min={0} max={50} value={form.tax?.gst_rate ?? 18} onChange={(e) => setSect("tax", "gst_rate", parseFloat(e.target.value || "0"))} data-testid="settings-gst-rate" className="bg-navy-900/60 border-white/10" /></Field>
          <Field label="TAX LABEL (shown on invoices)"><Input value={form.tax?.tax_label || ""} onChange={(e) => setSect("tax", "tax_label", e.target.value)} data-testid="settings-tax-label" className="bg-navy-900/60 border-white/10" /></Field>
        </div>
        <Field label="TAX NOTES (international/domestic treatment)">
          <Input value={form.tax?.notes || ""} onChange={(e) => setSect("tax", "notes", e.target.value)} data-testid="settings-tax-notes" className="bg-navy-900/60 border-white/10" />
        </Field>
        <p className="text-[11px] text-slate-500">Tax treatment is applied per this configuration. Obtain professional CA/tax review before production launch — international customers are not automatically GST-exempt.</p>
      </div>

      {/* Payments */}
      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 p-8 space-y-6" data-testid="settings-payments-section">
        <div className="flex items-center justify-between">
          <p className="font-heading font-bold text-white">Payments (Razorpay)</p>
          {payConfig && (
            <span className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-mono text-[10px] font-bold tracking-widest ${payConfig.payments_enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="settings-razorpay-status">
              {payConfig.payments_enabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
              {payConfig.payments_enabled ? "CONFIGURED" : "NOT CONFIGURED"}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET and RAZORPAY_WEBHOOK_SECRET are read from the server environment only and
          are never exposed to the frontend or stored in the database.
        </p>
        <label className="flex items-center gap-3 text-sm text-slate-200">
          <Switch checked={!!form.payments?.international_enabled} onCheckedChange={(v) => setSect("payments", "international_enabled", v)} data-testid="settings-intl-toggle" /> International payments enabled
        </label>
        <Field label="SUPPORTED PAYMENT CURRENCIES (INR is always enabled; others require international payments enabled on your Razorpay account)">
          <div className="flex flex-wrap gap-2 mt-1">
            {ALL_CURRENCIES.map((c) => (
              <button key={c} type="button" data-testid={`settings-pay-currency-${c}`} onClick={() => toggleIn("payments", "payment_currencies", c)}
                className={`rounded-full px-4 py-2 font-mono text-[11px] font-bold tracking-wider transition-colors ${(form.payments?.payment_currencies || []).includes(c) ? "bg-emerald-600 text-white" : "border border-white/15 text-slate-400"}`}>
                {c}
              </button>
            ))}
          </div>
        </Field>
        <p className="text-[11px] text-slate-500">If a currency is not enabled here, checkout in that currency is blocked and the customer is asked to select another.</p>
      </div>

      <Button onClick={save} disabled={busy} data-testid="settings-save-btn" className="mt-8 bg-flame hover:bg-flame-hover text-white font-heading font-bold px-10 h-12">
        {busy ? "SAVING…" : "SAVE SETTINGS"}
      </Button>

      <div className="mt-10 rounded-xl border border-amber-500/30 bg-amber-500/5 p-8">
        <p className="font-heading font-bold text-amber-400">Demo data</p>
        <p className="mt-2 text-xs text-slate-400 leading-relaxed">
          The site ships with clearly-labelled DEMO leads, Rush Hour bookings and case studies for testing. Remove them
          before going live. This action is permanent.
        </p>
        <Button onClick={purgeDemo} data-testid="settings-purge-demo-btn" variant="outline" className="mt-5 border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
          <Trash2 className="w-4 h-4 mr-2" /> REMOVE ALL DEMO DATA
        </Button>
      </div>
    </div>
  );
}
